import { Button } from '@ambernord/ui';
import { type FormEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext.js';
import { AppHeader } from '../components/AppHeader.js';
import { TurnstileWidget } from '../components/TurnstileWidget.js';
import { playAudioCue } from '../lib/audio.js';
import { useTurnstile } from '../lib/useTurnstile.js';

export default function Login() {
  const { t } = useTranslation('auth');
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const turnstile = useTurnstile();

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!turnstile.token) return;
    setError(null);
    setSubmitting(true);
    try {
      const res = await login(email, password, turnstile.token);
      if (!res.ok) {
        // Turnstile tokens ir vienreizējs — pēc jebkuras neveiksmes vajag jaunu.
        turnstile.reset();
        setError(
          res.error === 'turnstile_failed'
            ? t('turnstile.failed')
            : (res.error ?? t('status.error')),
        );
        playAudioCue('error');
        return;
      }
      playAudioCue('success');
      navigate(res.mfaRequired ? '/mfa' : '/', { replace: true });
    } catch {
      turnstile.reset();
      setError(t('status.error'));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="min-h-full flex flex-col">
      <AppHeader />
      <main className="mx-auto flex w-full max-w-md flex-1 flex-col justify-center px-4 py-10">
        <section className="rounded-lg border border-slate-200 p-6 dark:border-slate-800">
          <h2 className="mb-4 text-base font-medium">{t('login.heading')}</h2>
          <form className="flex flex-col gap-4" onSubmit={(e) => void onSubmit(e)} noValidate>
            <div className="flex flex-col gap-1">
              <label className="text-sm font-medium" htmlFor="email">
                {t('login.email')}
              </label>
              <input
                id="email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="h-10 rounded-md border border-slate-300 bg-white px-3 text-sm dark:border-slate-700 dark:bg-slate-900"
              />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-sm font-medium" htmlFor="password">
                {t('login.password')}
              </label>
              <input
                id="password"
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="h-10 rounded-md border border-slate-300 bg-white px-3 text-sm dark:border-slate-700 dark:bg-slate-900"
              />
            </div>
            {error && (
              <p className="text-sm text-red-600 dark:text-red-400" role="alert">
                {error}
              </p>
            )}
            <TurnstileWidget turnstile={turnstile} />
            <Button type="submit" disabled={submitting || !turnstile.token}>
              {submitting ? t('status.loading') : t('login.submit')}
            </Button>
          </form>
          <p className="mt-4 flex flex-wrap gap-x-4 gap-y-1 text-sm text-slate-600 dark:text-slate-400">
            <Link className="text-brand-600 hover:underline dark:text-brand-400" to="/register">
              {t('login.register')}
            </Link>
            <Link
              className="text-brand-600 hover:underline dark:text-brand-400"
              to="/forgot-password"
            >
              {t('login.forgotPassword')}
            </Link>
          </p>
        </section>
      </main>
    </div>
  );
}
